import {
  fetchData,
  postData,
  patchData,
  deleteData } from 'modules/Axios';
import { showToast } from 'modules/Notifications';
import { resetTable } from 'modules/Table';
import { sortByPropAscendingCaseInsensitive } from 'lib/Sorting';

export const defaultState = {
  snowflakeConnections: [],
  snowflakeCurrentPage: 0,
  snowflakeTotalRecords: 100,
  thoughtSpotConnections: [],
  thoughtSpotTotalRecords: 100,
  thoughtSpotConnectionAvailableGroups: [],
  hexConnections: [],
  hexConnectionAvailableGroups: [],
  hexCurrentPage: 0,
  hexTotalRecords: 100,
};

export const RESET_CONNECTIONS_ROUTE_DATA = 'RESET_CONNECTIONS_ROUTE_DATA';
export const resetRouteData = () => ({
  type: RESET_CONNECTIONS_ROUTE_DATA,
});

export const RESET_SNOWFLAKE_CONNECTIONS = 'RESET_SNOWFLAKE_CONNECTIONS';
export const RESET_THOUGHTSPOT_CONNECTIONS = 'RESET_THOUGHTSPOT_CONNECTIONS';
export const RESET_HEX_CONNECTIONS = 'RESET_HEX_CONNECTIONS';

export const refetchExternalConnections = (connectionType) => {
  return (dispatch) => {
    switch (connectionType) {
      case SNOWFLAKE:
        dispatch({ type: RESET_SNOWFLAKE_CONNECTIONS });
        return dispatch(resetTable('snowflakeConnections'));
      case HEX:
        dispatch({ type: RESET_HEX_CONNECTIONS });
        return dispatch(resetTable('hexConnections'));
      case THOUGHTSPOT:
        dispatch({ type: RESET_THOUGHTSPOT_CONNECTIONS });
        return dispatch(getThoughtSpotConnections());
      default:
        console.error('Invalid integration type:', connectionType);
    }
  };
};

export const createExternalConnection = (requestBody, connectionType) => {
  return async (dispatch) => {
    try {
      await dispatch(postData(`/connections/${connectionType}`, requestBody));
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: 'Connection has been created',
          dismissible: true,
          dismissAfter: 2000,
        })
      );
      dispatch(refetchExternalConnections(connectionType));
    } catch (e) {
      console.error(`API Call to /connections/${connectionType} failed...`, e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: `Issue creating the new ${connectionType} connection. Please try again later`,
          dismissible: true,
          dismissAfter: 1500,
        })
      );
    }
  };
};

export const updateExternalConnection = (connectionId, requestBody, connectionType) => {
  return async (dispatch) => {
    try {
      await dispatch(patchData(`/connections/${connectionType}/${connectionId}`, requestBody));
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: 'Connection has been updated',
          dismissible: true,
          dismissAfter: 2000,
        })
      );
      dispatch(refetchExternalConnections(connectionType));
    } catch (e) {
      console.error(`API Call to /connections/${connectionType} failed...`, e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: `Issue updating the ${connectionType} connection. Please try again later`,
          dismissible: true,
          dismissAfter: 1500,
        })
      );
    }
  };
};

export const removeExternalConnection = (connectionId, connectionType) => {
  return async (dispatch) => {
    try {
      await dispatch(deleteData(`/connections/${connectionType}/${connectionId}`));
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: 'Connection has been deleted',
          dismissible: true,
          dismissAfter: 2000,
        })
      );
      dispatch(refetchExternalConnections(connectionType));
    } catch (e) {
      if (e.response.status === 424) {
        return e.response.data.blocked_by;
      }
      console.error(`API Call to /connections/${connectionType} failed...`, e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: `Issue removing the ${connectionType} connection. Please try again later`,
          dismissible: true,
          dismissAfter: 1500,
        })
      );
    }
  };
};

// SNOWFLAKE CONNECTIONS
const SNOWFLAKE = 'snowflake';

export const UPDATE_SNOWFLAKE_CONNECTIONS_PAGE = 'UPDATE_SNOWFLAKE_CONNECTIONS_PAGE';
export const updateSnowflakeConnectionsPage = (page) => ({
  type: UPDATE_SNOWFLAKE_CONNECTIONS_PAGE,
  payload: page,
});

export const SET_SNOWFLAKE_CONNECTIONS = 'SET_SNOWFLAKE_CONNECTIONS';
export const setSnowflakeConnections = (connections) => ({
  type: SET_SNOWFLAKE_CONNECTIONS,
  payload: connections,
});

export const GET_SNOWFLAKE_CONNECTIONS = 'GET_SNOWFLAKE_CONNECTIONS';
export const getSnowflakeConnections = () => {
  return async (dispatch) => {
    try {
      const connections = await dispatch(fetchData('/connections/snowflake?size=100000'));
      dispatch(
        setSnowflakeConnections({
          snowflakeConnections: connections.content,
          snowflakeTotalRecords: connections.total_elements,
        })
      );
    } catch (e) {
      console.error('API Call to /connections/snowflake failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue retrieving snowflake connections list. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
      dispatch(
        setSnowflakeConnections({
          snowflakeConnections: defaultState.snowflakeConnections,
          snowflakeTotalRecords: 0,
        })
      );
    }
  };
};

export const createSnowflakeConnection = (requestBody) => {
  return createExternalConnection(requestBody, SNOWFLAKE);
};

export const updateSnowflakeConnection = (connectionId, requestBody) => {
  return updateExternalConnection(connectionId, requestBody, SNOWFLAKE);
};

export const removeSnowflakeConnection = (connectionId) => {
  return removeExternalConnection(connectionId, SNOWFLAKE);
};

// HEX CONNECTIONS
const HEX = 'hex';

export const UPDATE_HEX_CONNECTIONS_PAGE = 'UPDATE_HEX_CONNECTIONS_PAGE';
export const updateHexConnectionsPage = (page) => ({
  type: UPDATE_HEX_CONNECTIONS_PAGE,
  payload: page,
});

export const SET_HEX_CONNECTIONS = 'SET_HEX_CONNECTIONS';
export const setHexConnections = (connections) => ({
  type: SET_HEX_CONNECTIONS,
  payload: connections,
});

export const getHexConnections = (options) => {
  const size = options?.size || 25;

  return async (dispatch, getState) => {
    const { hexCurrentPage, hexConnections } = getState().connections;
    const URL = `/connections/hex?size=${size}&page=${hexCurrentPage}`;
    try {
      const connections = await dispatch(fetchData(URL));
      dispatch(updateHexConnectionsPage(hexCurrentPage + 1));
      dispatch(
        setHexConnections({
          hexConnections: hexConnections.concat(connections.content),
          hexTotalRecords: connections.total_elements,
        })
      );
    } catch (e) {
      console.error('API Call to /connections/hex failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue retrieving Hex connections list. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
      dispatch(
        setHexConnections({
          hexConnections: defaultState.hexConnections,
          hexTotalRecords: 0,
        })
      );
    }
  };
};

export const SET_HEX_CONNECTION_AVAILABLE_GROUPS = 'SET_HEX_CONNECTION_AVAILABLE_GROUPS';
export const setHexConnectionAvailableGroups = (payload) => ({
  type: SET_HEX_CONNECTION_AVAILABLE_GROUPS,
  payload,
});

export const GET_HEX_CONNECTION_AVAILABLE_GROUPS = 'GET_HEX_CONNECTION_AVAILABLE_GROUPS';
export const getHexConnectionAvailableGroups = (connectionId) => {
  return async (dispatch) => {
    try {
      if (connectionId) {
        const availableGroups = await dispatch(fetchData(`/connections/hex/${connectionId}/groups`));
        dispatch(setHexConnectionAvailableGroups(availableGroups));
      }
    } catch (e) {
      console.error('Issue retrieving available groups for Hex connection', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue retrieving available groups for Hex connection. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
      dispatch(setHexConnectionAvailableGroups([]));
    }
  };
};

export const createHexConnection = (requestBody) => {
  return createExternalConnection(requestBody, HEX);
};

export const updateHexConnection = (connectionId, requestBody) => {
  return updateExternalConnection(connectionId, requestBody, HEX);
};

export const removeHexConnection = (connectionId) => {
  return removeExternalConnection(connectionId, HEX);
};

// THOUGHTSPOT CONNECTIONS
const THOUGHTSPOT = 'thoughtspot';

export const SET_THOUGHTSPOT_CONNECTIONS = 'SET_THOUGHTSPOT_CONNECTIONS';
export const setThoughtSpotConnections = (connections) => ({
  type: SET_THOUGHTSPOT_CONNECTIONS,
  payload: connections,
});

export const getThoughtSpotConnections = () => {
  return async (dispatch) => {
    try {
      // Include brand information with connections -- REST APIs don't include
      // this information, so we must build it ourselves.
      const [connections, brands] = await Promise.all([
        dispatch(fetchData('/connections/thoughtspot?size=100000')),
        dispatch(fetchData('/brands/extended?size=100000')),
      ]);

      const brandsBySid = Object.fromEntries(
        brands?.content?.map((brand) => [brand.sid, brand])
      );

      const connectionsWithBrandsArray = connections?.content?.map((conn) => ({
        ...conn,
        brands: conn?.connection_brands?.map((sid) => brandsBySid[sid] ?? { sid }),
      }));

      dispatch(setThoughtSpotConnections(connectionsWithBrandsArray));
      return connectionsWithBrandsArray;
    } catch (e) {
      console.error('API calls to get ThoughtSpot connnection details failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue retrieving ThoughtSpot connections list. Please try again later',
          dismissible: true,
          dismissAfter: 5000,
        })
      );
      dispatch(setThoughtSpotConnections(defaultState.thoughtSpotConnections));
      return null;
    }
  };
};

export const getTsPrimaryConnections = () => {
  return async (dispatch) => {
    const URL = '/connections/thoughtspot/primary';
    try {
      const connections = await dispatch(fetchData(URL));
      return connections;
    } catch (e) {
      console.error('API Call to /connections/thoughtspot failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue retrieving ThoughtSpot connections list. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
      return [];
    }
  };
};

export const getTsOrganizations = (connectionId) => {
  return async (dispatch) => {
    const URL = `/connections/thoughtspot/${connectionId}/organizations`;
    try {
      const tsOrganizations = await dispatch(fetchData(URL));
      return tsOrganizations;
    } catch (e) {
      console.error('API Call to /connections/thoughtspot failed...', e);
      return [];
    }
  };
};

export const SET_THOUGHTSPOT_CONNECTION_AVAILABLE_GROUPS = 'SET_THOUGHTSPOT_CONNECTION_AVAILABLE_GROUPS';
export const setThoughtSpotConnectionAvailableGroups = (payload) => ({
  type: SET_THOUGHTSPOT_CONNECTION_AVAILABLE_GROUPS,
  payload,
});

export const GET_THOUGHTSPOT_CONNECTION_AVAILABLE_GROUPS = 'GET_THOUGHTSPOT_CONNECTION_AVAILABLE_GROUPS';
export const getThoughtSpotConnectionAvailableGroups = (connectionId) => {
  return async (dispatch) => {
    try {
      if (connectionId) {
        const availableGroups = await dispatch(fetchData(`/connections/thoughtspot/${connectionId}/groups`));
        dispatch(setThoughtSpotConnectionAvailableGroups(availableGroups));
      }
    } catch (e) {
      console.error('Issue retrieving available groups for TS connection', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue retrieving available groups for TS connection. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
      dispatch(setThoughtSpotConnectionAvailableGroups([]));
    }
  };
};

export const createThoughtSpotConnection = (requestBody) => {
  return createExternalConnection(requestBody, THOUGHTSPOT);
};

export const updateThoughtSpotConnection = (connectionId, requestBody) => {
  return updateExternalConnection(connectionId, requestBody, THOUGHTSPOT);
};

export const removeThoughtSpotConnection = (connectionId) => {
  return removeExternalConnection(connectionId, THOUGHTSPOT);
};

export default function reducer(state = defaultState, action = {}) {
  switch (action.type) {
    case SET_SNOWFLAKE_CONNECTIONS:
      return {
        ...state,
        snowflakeConnections: action.payload.snowflakeConnections,
        snowflakeTotalRecords: action.payload.snowflakeTotalRecords,
      };
    case RESET_SNOWFLAKE_CONNECTIONS:
      return {
        ...state,
        snowflakeCurrentPage: 0,
        snowflakeTotalRecords: defaultState.snowflakeTotalRecords,
        snowflakeConnections: [],
      };
    case UPDATE_SNOWFLAKE_CONNECTIONS_PAGE:
      return {
        ...state,
        snowflakeCurrentPage: action.payload };
    case SET_HEX_CONNECTIONS:
      return {
        ...state,
        hexConnections: action.payload.hexConnections,
        hexTotalRecords: action.payload.hexTotalRecords,
      };
    case RESET_HEX_CONNECTIONS:
      return {
        ...state,
        hexCurrentPage: 0,
        hexTotalRecords: defaultState.hexTotalRecords,
        hexConnections: [],
      };
    case SET_HEX_CONNECTION_AVAILABLE_GROUPS:
      return {
        ...state,
        hexConnectionAvailableGroups: action.payload,
      };
    case UPDATE_HEX_CONNECTIONS_PAGE:
      return {
        ...state,
        hexCurrentPage: action.payload };
    case SET_THOUGHTSPOT_CONNECTIONS:
      return {
        ...state,
        thoughtSpotConnections: sortByPropAscendingCaseInsensitive(action.payload, 'name'),
        thoughtSpotTotalRecords: action.payload?.length ?? 0,
      };
    case SET_THOUGHTSPOT_CONNECTION_AVAILABLE_GROUPS:
      return {
        ...state,
        thoughtSpotConnectionAvailableGroups: action.payload,
      };
    case RESET_THOUGHTSPOT_CONNECTIONS:
      return {
        ...state,
        thoughtSpotConnections: defaultState.thoughtSpotConnections,
        thoughtSpotTotalRecords: defaultState.thoughtSpotTotalRecords,
      };
    case RESET_CONNECTIONS_ROUTE_DATA:
      return { ...defaultState };
    default:
      return state;
  }
}
